import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { ClipboardList, Lock, CheckCircle2, Loader2, Quote } from "lucide-react";
import { toast } from "sonner";

const LIKERT = [
    { v: 1, l: "Strongly disagree" }, { v: 2, l: "Disagree" }, { v: 3, l: "Neutral" },
    { v: 4, l: "Agree" }, { v: 5, l: "Strongly agree" },
];
const ETHNICITIES = ["Black African", "Black Caribbean", "Asian Indian", "Asian Pakistani", "Asian Bangladeshi", "Chinese", "Mixed / Multiple", "Other ethnic group", "Prefer not to say"];
const BANDS = ["Band 1–4", "Band 5–6", "Band 7–8a", "Band 8b+ / VSM", "Medical & Dental", "Other"];
const scoreColor = (a) => (a >= 4 ? "#00A499" : a >= 3 ? "#FFB81C" : "#DA291C");

function TakeSurvey({ survey, onDone }) {
    const [answers, setAnswers] = useState({});
    const [demo, setDemo] = useState({ ethnicity: "", band: "" });
    const [submitting, setSubmitting] = useState(false);
    const setA = (qid, val) => setAnswers((a) => ({ ...a, [qid]: val }));

    const submit = async () => {
        const missing = survey.questions.filter((q) => q.type !== "text" && answers[q.id] == null);
        if (missing.length) { toast.error("Please answer all rating questions"); return; }
        setSubmitting(true);
        try {
            await api.post(`/surveys/${survey.id}/responses`, { answers, ethnicity: demo.ethnicity || null, band: demo.band || null });
            toast.success("Thank you — your anonymous response was recorded");
            onDone();
        } catch { toast.error("Could not submit response"); }
        finally { setSubmitting(false); }
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="take-survey">
            <div className="rounded-lg bg-nhs-teal/10 border border-nhs-teal/20 p-4 flex items-start gap-3">
                <Lock className="h-5 w-5 text-nhs-teal shrink-0 mt-0.5" />
                <p className="text-sm text-nhs-ink">{survey.description}</p>
            </div>

            {survey.questions.map((q, idx) => (
                <div key={q.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid={`question-${q.id}`}>
                    <p className="font-heading font-semibold text-nhs-ink">
                        <span className="text-nhs-blue mr-2">{idx + 1}.</span>{q.prompt}
                    </p>
                    {q.type === "likert" && (
                        <div className="mt-4 grid grid-cols-5 gap-2">
                            {LIKERT.map((o) => {
                                const active = answers[q.id] === o.v;
                                return (
                                    <button key={o.v} type="button" onClick={() => setA(q.id, o.v)} data-testid={`likert-${q.id}-${o.v}`}
                                        className={`rounded-md border p-2 text-xs sm:text-sm font-medium transition-colors duration-200 ${active ? "bg-nhs-blue text-white border-nhs-blue" : "bg-white border-slate-300 text-slate-600 hover:border-nhs-blue hover:text-nhs-blue"}`}>
                                        <span className="block text-base font-bold">{o.v}</span>{o.l}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {q.type === "single_choice" && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {q.options.map((o) => {
                                const active = answers[q.id] === o;
                                return (
                                    <button key={o} type="button" onClick={() => setA(q.id, o)} data-testid={`choice-${q.id}-${o}`}
                                        className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors duration-200 ${active ? "bg-nhs-blue text-white border-nhs-blue" : "bg-white border-slate-300 text-slate-600 hover:border-nhs-blue"}`}>
                                        {o}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {q.type === "text" && (
                        <Textarea rows={3} className="mt-4" value={answers[q.id] || ""} onChange={(e) => setA(q.id, e.target.value)} placeholder="Share your thoughts…" data-testid={`text-${q.id}`} />
                    )}
                </div>
            ))}

            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <p className="font-heading font-semibold text-nhs-ink">Optional anonymous demographics</p>
                <p className="text-sm text-slate-500 mt-1">Helps us understand experience across groups. Never linked to you individually.</p>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                    <div>
                        <Label>Ethnicity</Label>
                        <Select value={demo.ethnicity} onValueChange={(v) => setDemo((d) => ({ ...d, ethnicity: v }))}>
                            <SelectTrigger className="mt-1.5" data-testid="survey-ethnicity"><SelectValue placeholder="Prefer not to say" /></SelectTrigger>
                            <SelectContent>{ETHNICITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Agenda for Change band</Label>
                        <Select value={demo.band} onValueChange={(v) => setDemo((d) => ({ ...d, band: v }))}>
                            <SelectTrigger className="mt-1.5" data-testid="survey-band"><SelectValue placeholder="Prefer not to say" /></SelectTrigger>
                            <SelectContent>{BANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            <Button onClick={submit} disabled={submitting} className="bg-nhs-blue hover:bg-nhs-darkblue text-white w-full sm:w-auto px-8" data-testid="submit-survey-button">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit anonymously"}
            </Button>
        </div>
    );
}

function SurveyResults({ survey }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get(`/surveys/${survey.id}/results`).then((r) => setData(r.data)).catch(() => {}); }, [survey.id]);
    if (!data) return <div className="text-slate-500" data-testid="results-loading">Loading results…</div>;

    const likert = data.results.filter((r) => r.type === "likert");
    const bars = likert.map((r) => ({ name: r.prompt.length > 26 ? r.prompt.slice(0, 26) + "…" : r.prompt, avg: r.average }));

    return (
        <div className="space-y-6" data-testid="survey-results">
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 flex items-center justify-between">
                <div>
                    <p className="text-sm text-slate-500">Anonymous responses</p>
                    <p className="font-heading text-3xl font-extrabold text-nhs-ink" data-testid="response-count">{data.response_count}</p>
                </div>
                <ClipboardList className="h-10 w-10 text-nhs-blue/30" />
            </div>

            {data.response_count === 0 ? (
                <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-400" data-testid="no-responses">No responses yet.</div>
            ) : (
                <>
                    <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                        <h3 className="font-heading font-semibold text-lg text-nhs-ink mb-4">Average agreement scores (1–5)</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={bars} margin={{ bottom: 50 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-18} textAnchor="end" height={70} interval={0} />
                                <YAxis domain={[0, 5]} tick={{ fontSize: 12 }} />
                                <Tooltip />
                                <Bar dataKey="avg" radius={[4, 4, 0, 0]}>
                                    {bars.map((b, i) => <Cell key={i} fill={scoreColor(b.avg)} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                        <p className="text-xs text-slate-400 mt-3">Green ≥4 (healthy) · Amber ≥3 (watch) · Red &lt;3 (priority area).</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {data.results.filter((r) => r.type === "single_choice").map((r) => {
                            const total = Object.values(r.counts).reduce((a, b) => a + b, 0) || 1;
                            return (
                                <div key={r.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid={`result-${r.id}`}>
                                    <h4 className="font-semibold text-nhs-ink text-sm">{r.prompt}</h4>
                                    <div className="mt-4 space-y-3">
                                        {Object.entries(r.counts).map(([k, v]) => (
                                            <div key={k}>
                                                <div className="flex justify-between text-sm"><span className="text-slate-600">{k}</span><span className="font-medium text-nhs-ink">{v}</span></div>
                                                <div className="h-2 bg-slate-100 rounded-full mt-1"><div className="h-2 bg-nhs-blue rounded-full" style={{ width: `${(v / total) * 100}%` }} /></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}

                        {data.ethnicity_breakdown.length > 0 && (
                            <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid="result-ethnicity">
                                <h4 className="font-semibold text-nhs-ink text-sm">Responses by ethnicity</h4>
                                <ResponsiveContainer width="100%" height={200}>
                                    <BarChart data={data.ethnicity_breakdown} layout="vertical" margin={{ left: 10 }}>
                                        <XAxis type="number" hide /><YAxis type="category" dataKey="group" width={120} tick={{ fontSize: 10 }} />
                                        <Tooltip /><Bar dataKey="count" fill="#00A499" radius={[0, 4, 4, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>

                    {data.results.filter((r) => r.type === "text" && r.answers.length > 0).map((r) => (
                        <div key={r.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-6" data-testid={`result-${r.id}`}>
                            <h4 className="font-semibold text-nhs-ink text-sm mb-4">{r.prompt}</h4>
                            <div className="space-y-3">
                                {r.answers.map((t, i) => (
                                    <div key={i} className="flex gap-2 text-sm text-slate-700 bg-slate-50 rounded-md p-3"><Quote className="h-4 w-4 text-nhs-teal shrink-0 mt-0.5" />{t}</div>
                                ))}
                            </div>
                        </div>
                    ))}
                </>
            )}
        </div>
    );
}

export default function CultureSurvey() {
    const { user } = useAuth();
    const isMgr = ["hr_leadership", "admin"].includes(user?.role);
    const [survey, setSurvey] = useState(null);
    const [completed, setCompleted] = useState(false);

    const load = () => api.get("/surveys/active").then((r) => { setSurvey(r.data); setCompleted(r.data.already_completed); }).catch(() => {});
    useEffect(() => { load(); }, []);

    if (!survey) return <div className="text-slate-500" data-testid="survey-loading">Loading survey…</div>;

    const takePanel = completed ? (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-10 text-center max-w-xl" data-testid="already-completed">
            <div className="h-14 w-14 rounded-full bg-nhs-teal/10 flex items-center justify-center mx-auto"><CheckCircle2 className="h-7 w-7 text-nhs-teal" /></div>
            <h3 className="font-heading text-xl font-bold text-nhs-ink mt-4">Thank you for your response</h3>
            <p className="text-slate-500 mt-2">You've completed the current culture pulse. Your feedback helps shape a fairer workplace.</p>
            <Button variant="outline" className="mt-6 border-slate-300" onClick={() => setCompleted(false)} data-testid="retake-survey">Submit another response</Button>
        </div>
    ) : (
        <TakeSurvey survey={survey} onDone={() => setCompleted(true)} />
    );

    return (
        <div className="space-y-6" data-testid="survey-page">
            <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-lg bg-nhs-blue/10 flex items-center justify-center"><ClipboardList className="h-5 w-5 text-nhs-blue" /></div>
                <div>
                    <h1 className="font-heading text-3xl font-extrabold text-nhs-ink">{survey.title}</h1>
                    <p className="text-slate-500">Anonymous survey shaping our inclusion culture.</p>
                </div>
            </div>

            {isMgr ? (
                <Tabs defaultValue="results">
                    <TabsList data-testid="survey-tabs">
                        <TabsTrigger value="results" data-testid="tab-results">Results</TabsTrigger>
                        <TabsTrigger value="take" data-testid="tab-take">Take survey</TabsTrigger>
                    </TabsList>
                    <TabsContent value="results" className="mt-6"><SurveyResults survey={survey} /></TabsContent>
                    <TabsContent value="take" className="mt-6">{takePanel}</TabsContent>
                </Tabs>
            ) : takePanel}
        </div>
    );
}
